import { create } from 'zustand';
import { OrdemServico, FaseOS, FamilyAccess, RealTimeUpdate } from '../types';
import api from '../utils/api';

// A posição do motorista é atualizada pelo próprio FaseMap; trocar o objeto da fase recria o mapa.
const chaveFase = (fase: FaseOS): string => {
  const { ultima_lat, ultima_lng, ultima_atualizacao, ...resto } = fase;
  return JSON.stringify({ ...resto, temPosicao: Boolean(ultima_lat && ultima_lng) });
};

const mesclarFases = (anteriores: FaseOS[], novas: FaseOS[]): FaseOS[] =>
  novas.map((nova) => {
    const anterior = anteriores.find((f) => f.id === nova.id);
    return anterior && chaveFase(anterior) === chaveFase(nova) ? anterior : nova;
  });

interface FamilyStore {
  // Estado
  codigoAcesso: string;
  ordemServico: OrdemServico | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  realTimeUpdates: RealTimeUpdate[];
  
  // Actions
  setCodigoAcesso: (codigo: string) => void;
  buscarOSPorNota: (numeroNota: string, digitosCelular: string) => Promise<boolean>;
  recarregarOS: () => Promise<void>;
  limparOS: () => void;
  adicionarUpdate: (update: RealTimeUpdate) => void;
  limparUpdates: () => void;
  clearError: () => void;
}

export const useFamilyStore = create<FamilyStore>((set, get) => ({
  // Estado inicial
  codigoAcesso: '',
  ordemServico: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  realTimeUpdates: [],

  // Definir código de acesso
  setCodigoAcesso: (codigo: string) => {
    set({ codigoAcesso: codigo });
  },

  // Buscar OS pelo número da Nota de Contratação + 4 últimos dígitos do celular do contratante
  buscarOSPorNota: async (numeroNota: string, digitosCelular: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await api.post('/os/public/acesso', {
        numero_nota_contratacao: numeroNota.replace(/\s+/g, ''),
        digitos_celular: digitosCelular.replace(/\D/g, '')
      });
      
      if (response.data.os) {
        const os = response.data.os;
        console.log('✅ OS encontrada:', os);
        
        set({
          ordemServico: os,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        
        return true;
      } else {
        set({
          error: 'Nota de Contratação não encontrada',
          isLoading: false,
          isAuthenticated: false
        });
        return false;
      }
      
    } catch (error: any) {
      console.error('❌ Erro ao buscar OS:', error);
      
      let errorMessage = 'Erro ao buscar OS';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      set({
        error: errorMessage,
        isLoading: false,
        isAuthenticated: false
      });
      
      return false;
    }
  },

  // Recarregar a OS já acessada (silencioso: falhas mantêm os dados atuais)
  recarregarOS: async () => {
    const atual = get().ordemServico;
    if (!atual?.codigo_acesso) return;

    try {
      const response = await api.get(`/os/public/${atual.codigo_acesso}`);
      const nova: OrdemServico | undefined = response.data?.os;
      const vigente = get().ordemServico;
      if (!nova || !vigente || vigente.codigo_acesso !== atual.codigo_acesso) return;

      set({
        ordemServico: {
          ...nova,
          fases: mesclarFases(vigente.fases || [], nova.fases || [])
        }
      });
    } catch (error) {
      console.warn('⚠️ Não foi possível recarregar a OS:', error);
    }
  },

  // Limpar OS atual
  limparOS: () => {
    set({
      ordemServico: null,
      isAuthenticated: false,
      codigoAcesso: '',
      realTimeUpdates: []
    });
  },

  // Adicionar atualização em tempo real
  adicionarUpdate: (update: RealTimeUpdate) => {
    set((state) => ({
      realTimeUpdates: [...state.realTimeUpdates, update]
    }));
    
    // Manter apenas as últimas 50 atualizações
    const { realTimeUpdates } = get();
    if (realTimeUpdates.length > 50) {
      set({
        realTimeUpdates: realTimeUpdates.slice(-50)
      });
    }
  },

  // Limpar atualizações
  limparUpdates: () => {
    set({ realTimeUpdates: [] });
  },

  // Limpar erro
  clearError: () => {
    set({ error: null });
  },
}));
